// useSettlementForm.js — 정산지급 기록 입력폼 상태 + 저장/삭제 핸들러
//
// useAdminSettlement에서 분리 — companyCode를 body에 포함하여
// 단일 테이블(marketer_settlement_records)에서 회사별 구분 가능하게 처리

import { useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import { fmt, today, makeToast } from './utils';
import { toDateString } from '../../../utils/date';

/**
 * @param {string}   prefix      - API prefix (/api/admin/settlement 등)
 * @param {string}   companyCode - 회사 코드 ('PINE' | 'LNJ' | 'DOT' | null)
 * @param {string}   apiBase     - DOT 여부 ('dot' | '')
 * @param {object}   selectedUser - 선택된 마케터 { id, name }
 * @param {function} loadRecords  - 기록 재로드 함수
 */
export function useSettlementForm({ prefix, companyCode, apiBase, selectedUser, loadRecords }) {
  const { showToast } = useToast();
  const toast = makeToast(showToast);

  const [editId,   setEditId]   = useState(null);   // null=신규, 숫자=수정 중
  const [formDate, setFormDate] = useState(today());
  const [formAmt,  setFormAmt]  = useState('');
  const [formNote, setFormNote] = useState('');
  const [saving,   setSaving]   = useState(false);

  // ── 폼 초기화 ─────────────────────────────────────────────────────────────
  function resetForm() {
    setEditId(null);
    setFormDate(today());
    setFormAmt('');
    setFormNote('');
  }

  // ── 기록 행 클릭 → 수정 모드 ────────────────────────────────────────────
  function selectRecord(rec) {
    setEditId(rec.id);
    setFormDate(toDateString(rec.settlement_date));
    setFormAmt(fmt(rec.amount));
    setFormNote(rec.note || '');
  }

  // ── 금액 입력 콤마 포맷 ──────────────────────────────────────────────────
  function onAmtChange(e) {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    setFormAmt(raw ? fmt(raw) : '');
  }

  // ── 저장 (신규/수정) ──────────────────────────────────────────────────────
  async function handleSave() {
    if (!selectedUser) { toast.error('마케터를 먼저 선택해 주세요.'); return; }
    if (!formDate)     { toast.error('날짜를 입력해 주세요.'); return; }
    if (!formAmt)      { toast.error('정산금액을 입력해 주세요.'); return; }
    setSaving(true);
    try {
      const payload = {
        settlementDate: formDate,
        amount:         Number(formAmt.replace(/,/g, '')),
        note:           formNote || null,
      };
      // 모든 회사 공통 endpoint — companyCode를 body에 포함하여 구분
      const postBody = {
        userId: selectedUser.id,
        ...payload,
        ...(companyCode ? { companyCode } : {}),
      };
      const res = editId
        ? await apiClient.put(`${prefix}/records/${editId}`, payload)
        : await apiClient.post(`${prefix}/records`, postBody);
      if (!res.ok) {
        toast.error(res.data?.message || '저장에 실패했습니다.');
      } else {
        toast.success(editId ? '수정됐습니다.' : '정산이 추가됐습니다.');
        resetForm();
        loadRecords(selectedUser.id);
      }
    } finally {
      setSaving(false);
    }
  }

  // ── 삭제 ──────────────────────────────────────────────────────────────────
  async function handleDelete(id) {
    if (!window.confirm('이 정산 기록을 삭제하시겠습니까?')) return;
    const res = await apiClient.del(`${prefix}/records/${id}`);
    if (res.ok) {
      toast.success('삭제됐습니다.');
      if (editId === id) resetForm();
      loadRecords(selectedUser.id);
    } else {
      toast.error(res.data?.message || '삭제에 실패했습니다.');
    }
  }

  return {
    editId, formDate, setFormDate, formAmt, formNote, setFormNote, saving,
    resetForm, selectRecord, onAmtChange, handleSave, handleDelete,
  };
}
